/**
 * /mail/announcements - operator announcements (OCP-633).
 *
 * Compose in both languages, pick the audience, PREVIEW (the api resolves
 * the recipients and returns a confirmation token bound to the texts and
 * the exact list), then send. History below with per-recipient outcome.
 *
 * Same endpoints as `ocp_send_announcement` / `ocp_list_announcements`:
 *   POST /v1/admin/mail/announcements/preview
 *   POST /v1/admin/mail/announcements       (confirmation_token)
 *   GET  /v1/admin/mail/announcements[/:id]
 *
 * OCP-928 - the bodies name their format (plain text, Markdown, HTML).
 * "Preview mail" renders the exact de + en mails server-side
 * (`/announcements/render`, the send's own renderer, shown in sandboxed
 * frames with their text parts); "Send test to me" mails them to the
 * operator's own address (`/announcements/test-send`).
 */
import { useCallback, useEffect, useState } from 'react';

import { MAIL_FORMAT_HINT, MailFormatSelect, MailRenderPreview, type MailBodyFormat, type RenderedMailParts } from '../components/MailRenderPreview';
import { ApiError, api } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type Kind = 'service_notice' | 'marketing';
type Audience = 'tenants' | 'accounts' | 'explicit';

interface Recipient {
  email: string;
  locale: 'de' | 'en';
  tenant_id: string | null;
  tenant_subdomain: string | null;
  account_id: string | null;
  via: 'tenant' | 'account';
}
interface Excluded {
  email: string | null;
  tenant_id: string | null;
  account_id: string | null;
  reason: string;
}
interface Preview {
  kind: Kind;
  recipients: Recipient[];
  excluded: Excluded[];
  counts: { included: number; excluded: number; de: number; en: number };
  from: string;
  reply_to: string | null;
  weekly_limit: number;
  confirmation_token: string;
  confirmation_expires_at: string;
}
interface RecipientOutcome {
  email: string;
  locale: 'de' | 'en';
  tenant_subdomain: string | null;
  status: 'sent' | 'failed' | 'suppressed';
  message_id: string | null;
  error: string | null;
}
interface SendResult {
  id: string;
  status: string;
  counts: { recipients: number; sent: number; failed: number; suppressed: number };
  recipients: RecipientOutcome[];
}
interface HistoryItem {
  id: string;
  created_at: string;
  created_by: string;
  source: string;
  kind: Kind;
  subject_de: string;
  subject_en: string;
  /** OCP-928 - absent on rows the API served before the format existed. */
  format?: MailBodyFormat;
  status: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  suppressed_count: number;
}
interface HistoryDetail extends HistoryItem {
  body_de: string;
  body_en: string;
  from_address: string;
  reply_to: string | null;
  reason: string | null;
  recipients: RecipientOutcome[];
  excluded: Excluded[];
}
interface RenderResult {
  format: MailBodyFormat;
  de: RenderedMailParts;
  en: RenderedMailParts;
  unsubscribe_url: string;
  per_recipient_unsubscribe: boolean;
}
interface TestSendResult {
  sent: Array<{ locale: 'de' | 'en'; to: string; message_id: string }>;
  rate_limit: { used: number; limit: number };
}

function errorText(err: unknown): string {
  const body = err instanceof ApiError ? ((err.body as { error?: string; message?: string; announcement_id?: string; locale?: string; retry_after_seconds?: number } | null) ?? {}) : {};
  switch (body.error) {
    case 'empty_body':
      return `The ${body.locale ?? ''} body renders to no readable text. Every mail needs a readable text part.`;
    case 'rate_limited':
      return `Test-send limit reached (10 per hour). Try again in ${Math.ceil((body.retry_after_seconds ?? 0) / 60)} min.`;
    case 'no_operator_address':
      return 'Your operator account has no mailbox address for a test send.';
    case 'preview_stale':
      return 'The recipient list or the texts changed since the preview. Preview again.';
    case 'confirmation_expired':
      return 'The preview expired (10 minutes). Preview again.';
    case 'already_sent':
      return `This preview was already sent (announcement ${body.announcement_id ?? ''}).`;
    case 'invalid_audience':
      return body.message ?? 'Pick tenants and/or accounts for an explicit audience.';
    case 'confirmation_unavailable':
      return 'The api has no confirmation secret configured.';
    default:
      return body.message ?? `Request failed${body.error ? ` (${body.error})` : ''}.`;
  }
}

export function AdminMailAnnouncementsPage() {
  const [kind, setKind] = useState<Kind>('service_notice');
  const [subjectDe, setSubjectDe] = useState('');
  const [subjectEn, setSubjectEn] = useState('');
  const [bodyDe, setBodyDe] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [format, setFormat] = useState<MailBodyFormat>('text');
  const [rendered, setRendered] = useState<RenderResult | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [audience, setAudience] = useState<Audience>('tenants');
  const [tenantIds, setTenantIds] = useState('');
  const [accountIds, setAccountIds] = useState('');
  const [includeDemo, setIncludeDemo] = useState(false);
  const [selfHost, setSelfHost] = useState<'' | 'true' | 'false'>('');
  const [tenantLocale, setTenantLocale] = useState('');
  const [reason, setReason] = useState('');

  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState<'' | 'preview' | 'send' | 'render' | 'test'>('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [selected, setSelected] = useState<HistoryDetail | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api<{ items: HistoryItem[]; total: number }>('/v1/admin/mail/announcements?limit=50');
      setHistory(r.items);
      setHistoryError(null);
    } catch (err) {
      setHistoryError(err instanceof ApiError ? `Could not load history (HTTP ${err.status}).` : 'Could not load history.');
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // A rendered preview belongs to exactly the texts it was rendered from.
  useEffect(() => {
    setRendered(null);
  }, [kind, subjectDe, subjectEn, bodyDe, bodyEn, format]);

  function request() {
    const ids = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    const filters: Record<string, unknown> = {};
    if (audience === 'tenants') {
      if (includeDemo) filters.include_demo = true;
      if (selfHost) filters.is_self_host = selfHost === 'true';
      if (tenantLocale.trim()) filters.tenant_locale = tenantLocale.trim();
    }
    return {
      kind,
      subject_de: subjectDe.trim(),
      subject_en: subjectEn.trim(),
      body_de: bodyDe.trim(),
      body_en: bodyEn.trim(),
      format,
      audience,
      ...(audience === 'explicit' && ids(tenantIds).length ? { tenant_ids: ids(tenantIds) } : {}),
      ...(audience === 'explicit' && ids(accountIds).length ? { account_ids: ids(accountIds) } : {}),
      ...(Object.keys(filters).length ? { filters } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    };
  }

  const complete = subjectDe.trim() && subjectEn.trim() && bodyDe.trim() && bodyEn.trim();

  async function runPreview() {
    setBusy('preview');
    setError(null);
    setResult(null);
    setPreview(null);
    try {
      setPreview(await api<Preview>('/v1/admin/mail/announcements/preview', { method: 'POST', body: request() }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  async function send() {
    if (!preview) return;
    setBusy('send');
    setError(null);
    try {
      const r = await api<SendResult>('/v1/admin/mail/announcements', {
        method: 'POST',
        body: { ...request(), confirmation_token: preview.confirmation_token },
      });
      setResult(r);
      setPreview(null);
      await loadHistory();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  // OCP-928 - the exact de + en mails, rendered by the send's own renderer.
  async function renderMail() {
    setBusy('render');
    setError(null);
    try {
      setRendered(await api<RenderResult>('/v1/admin/mail/announcements/render', { method: 'POST', body: texts() }));
    } catch (err) {
      setRendered(null);
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  async function testSend() {
    setBusy('test');
    setError(null);
    setNote(null);
    try {
      const r = await api<TestSendResult>('/v1/admin/mail/announcements/test-send', { method: 'POST', body: texts() });
      setNote(
        `Test sent to your address ${r.sent[0]?.to ?? ''}: ${r.sent.map((s) => `${s.locale} ${s.message_id}`).join(', ')} (${r.rate_limit.used}/${r.rate_limit.limit} test mails this hour).`,
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  /** Render + test-send need only the texts; the audience stays out of it. */
  function texts() {
    return {
      kind,
      subject_de: subjectDe.trim(),
      subject_en: subjectEn.trim(),
      body_de: bodyDe.trim(),
      body_en: bodyEn.trim(),
      format,
    };
  }

  async function openDetail(id: string) {
    try {
      setSelected(await api<HistoryDetail>(`/v1/admin/mail/announcements/${id}`));
    } catch (err) {
      setHistoryError(errorText(err));
    }
  }

  const input = 'w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Announcements</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          One mail to many customer contacts, in each contact&apos;s language. Preview resolves the recipients; nothing goes out
          before you confirm the list. Same path as the MCP tool ocp_send_announcement.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm space-y-3" data-testid="announcement-compose">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Kind
            <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} className={input} aria-label="Kind">
              <option value="service_notice">service notice (product / service information, ignores marketing opt-out)</option>
              <option value="marketing">marketing (skips unsubscribed tenants, carries an unsubscribe link)</option>
            </select>
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Audience
            <select value={audience} onChange={(e) => setAudience(e.target.value as Audience)} className={input} aria-label="Audience">
              <option value="tenants">all live tenants (contact email)</option>
              <option value="accounts">all portal accounts (owner email)</option>
              <option value="explicit">explicit tenant / account ids</option>
            </select>
          </label>
        </div>
        {audience === 'tenants' && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-700 dark:text-gray-300">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} /> include demo tenant
            </label>
            <label className="flex items-center gap-1">
              self-host
              <select value={selfHost} onChange={(e) => setSelfHost(e.target.value as '' | 'true' | 'false')} className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-1 py-0.5 text-xs">
                <option value="">any</option>
                <option value="false">managed only</option>
                <option value="true">self-host only</option>
              </select>
            </label>
            <label className="flex items-center gap-1">
              tenant locale
              <input type="text" value={tenantLocale} onChange={(e) => setTenantLocale(e.target.value)} placeholder="de / en" className="w-16 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-1 py-0.5 text-xs" />
            </label>
          </div>
        )}
        {audience === 'explicit' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600 dark:text-gray-400">
              Tenant ids (one per line or comma separated)
              <textarea value={tenantIds} onChange={(e) => setTenantIds(e.target.value)} rows={3} className={input} aria-label="Tenant ids" />
            </label>
            <label className="text-xs text-gray-600 dark:text-gray-400">
              Account ids
              <textarea value={accountIds} onChange={(e) => setAccountIds(e.target.value)} rows={3} className={input} aria-label="Account ids" />
            </label>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          <span>Body format</span>
          <MailFormatSelect value={format} onChange={setFormat} />
          <span className="text-[11px] text-gray-500" data-testid="announcement-format-hint">
            {MAIL_FORMAT_HINT[format]}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Subject (de)
            <input type="text" value={subjectDe} onChange={(e) => setSubjectDe(e.target.value)} maxLength={200} className={input} aria-label="Subject de" />
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Subject (en)
            <input type="text" value={subjectEn} onChange={(e) => setSubjectEn(e.target.value)} maxLength={200} className={input} aria-label="Subject en" />
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Body (de)
            <textarea value={bodyDe} onChange={(e) => setBodyDe(e.target.value)} rows={8} className={`${input} font-mono`} aria-label="Body de" />
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Body (en)
            <textarea value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} rows={8} className={`${input} font-mono`} aria-label="Body en" />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void renderMail()}
            disabled={!complete || busy !== ''}
            data-testid="announcement-render"
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {busy === 'render' ? 'Rendering...' : 'Preview mail'}
          </button>
          <button
            type="button"
            onClick={() => void testSend()}
            disabled={!complete || busy !== ''}
            data-testid="announcement-test-send"
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {busy === 'test' ? 'Sending test...' : 'Send test to me (de + en)'}
          </button>
        </div>
        {rendered && (
          <div className="space-y-2" data-testid="announcement-rendered">
            {rendered.per_recipient_unsubscribe && (
              <p className="text-[11px] text-gray-500">
                Marketing: each tenant recipient gets their own unsubscribe link where this preview shows {rendered.unsubscribe_url}.
              </p>
            )}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <MailRenderPreview mail={rendered.de} label={`de (${rendered.format})`} testId="announcement-rendered-de" />
              <MailRenderPreview mail={rendered.en} label={`en (${rendered.format})`} testId="announcement-rendered-en" />
            </div>
          </div>
        )}
        {note && <p className="text-xs text-emerald-700 dark:text-emerald-300">{note}</p>}
        <div className="flex flex-wrap items-center gap-2">
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audit)" maxLength={2000} className="w-64 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs" aria-label="Reason" />
          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={!complete || busy !== ''}
            data-testid="announcement-preview"
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {busy === 'preview' ? 'Resolving...' : 'Preview recipients'}
          </button>
          {preview && (
            <button
              type="button"
              onClick={() => void send()}
              disabled={busy !== '' || preview.recipients.length === 0}
              data-testid="announcement-send"
              className="rounded bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {busy === 'send' ? 'Sending...' : `Send to ${preview.recipients.length} recipient${preview.recipients.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
        {error && (
          <p role="alert" className="text-xs text-red-700 dark:text-red-300">
            {error}
          </p>
        )}
      </div>

      {preview && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-xs" data-testid="announcement-preview-result">
          <div className="font-medium text-amber-900 dark:text-amber-200">
            {preview.counts.included} recipient{preview.counts.included === 1 ? '' : 's'} ({preview.counts.de} de, {preview.counts.en} en), {preview.counts.excluded} excluded. From {preview.from}, Reply-To {preview.reply_to ?? '(none)'}. Weekly cap {preview.weekly_limit}. Token valid until {fmtDateTime(preview.confirmation_expires_at)}.
          </div>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="font-medium text-gray-700 dark:text-gray-300">Included</div>
              <ul className="mt-1 max-h-64 overflow-auto font-mono">
                {preview.recipients.map((r) => (
                  <li key={r.email}>
                    {r.email} <span className="text-gray-500">({r.locale}{r.tenant_subdomain ? `, ${r.tenant_subdomain}` : r.via === 'account' ? ', account' : ''})</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-medium text-gray-700 dark:text-gray-300">Excluded</div>
              <ul className="mt-1 max-h-64 overflow-auto font-mono">
                {preview.excluded.map((x, i) => (
                  <li key={`${x.email ?? x.tenant_id ?? x.account_id ?? i}-${x.reason}`}>
                    {x.email ?? x.tenant_id ?? x.account_id} <span className="text-gray-500">{x.reason}</span>
                  </li>
                ))}
                {preview.excluded.length === 0 && <li className="text-gray-500">none</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4 text-xs" data-testid="announcement-result">
          <div className="font-medium text-emerald-900 dark:text-emerald-200">
            Announcement {result.id}: {result.status} - {result.counts.sent} sent, {result.counts.failed} failed, {result.counts.suppressed} suppressed.
          </div>
          <ul className="mt-1 font-mono">
            {result.recipients.map((r) => (
              <li key={r.email}>
                {r.email} <span className="text-gray-500">{r.locale} {r.status}{r.message_id ? ` ${r.message_id}` : ''}{r.error ? ` ${r.error}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">History</h2>
        {historyError && (
          <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">
            {historyError}
          </p>
        )}
        <div className="mt-2 overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-400">
              <tr>
                <th className="px-2 py-1">When</th>
                <th className="px-2 py-1">Kind (format)</th>
                <th className="px-2 py-1">Subject (de / en)</th>
                <th className="px-2 py-1">Recipients</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">By</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr
                  key={h.id}
                  onClick={() => void openDetail(h.id)}
                  data-testid={`announcement-row-${h.id}`}
                  className="cursor-pointer border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="px-2 py-1 whitespace-nowrap">{fmtDateTime(h.created_at)}</td>
                  <td className="px-2 py-1">
                    {h.kind} <span className="text-gray-500">({h.format ?? 'text'})</span>
                  </td>
                  <td className="px-2 py-1">
                    {h.subject_de} <span className="text-gray-500">/ {h.subject_en}</span>
                  </td>
                  <td className="px-2 py-1">
                    {h.sent_count}/{h.recipient_count}
                    {h.failed_count ? ` (${h.failed_count} failed)` : ''}
                    {h.suppressed_count ? ` (${h.suppressed_count} suppressed)` : ''}
                  </td>
                  <td className="px-2 py-1">{h.status}</td>
                  <td className="px-2 py-1">
                    {h.created_by} <span className="text-gray-500">({h.source})</span>
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-2 py-3 text-center text-gray-500">
                    No announcements sent through OCP yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-xs" data-testid="announcement-detail">
          <div className="flex items-center justify-between">
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {selected.subject_de} / {selected.subject_en}
            </div>
            <button type="button" onClick={() => setSelected(null)} className="text-gray-500 hover:underline">
              close
            </button>
          </div>
          <div className="mt-1 text-gray-600 dark:text-gray-400">
            {selected.kind}, {selected.format ?? 'text'} body, {fmtDateTime(selected.created_at)} by {selected.created_by} ({selected.source}). From {selected.from_address}, Reply-To {selected.reply_to ?? '(none)'}.{selected.reason ? ` Reason: ${selected.reason}` : ''}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            <pre className="whitespace-pre-wrap rounded bg-gray-50 dark:bg-gray-800 p-2">{selected.body_de}</pre>
            <pre className="whitespace-pre-wrap rounded bg-gray-50 dark:bg-gray-800 p-2">{selected.body_en}</pre>
          </div>
          <div className="mt-2 font-medium text-gray-700 dark:text-gray-300">Recipients</div>
          <ul className="mt-1 font-mono">
            {selected.recipients.map((r) => (
              <li key={r.email}>
                {r.email} <span className="text-gray-500">{r.locale} {r.status}{r.message_id ? ` ${r.message_id}` : ''}{r.error ? ` ${r.error}` : ''}</span>
              </li>
            ))}
          </ul>
          {selected.excluded.length > 0 && (
            <>
              <div className="mt-2 font-medium text-gray-700 dark:text-gray-300">Excluded at preview</div>
              <ul className="mt-1 font-mono">
                {selected.excluded.map((x, i) => (
                  <li key={`${x.email ?? x.tenant_id ?? x.account_id ?? i}-${x.reason}`}>
                    {x.email ?? x.tenant_id ?? x.account_id} <span className="text-gray-500">{x.reason}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
